import { AccountTabs } from '@/types/enums/forms/account/accountTabs';
import {
    ACCOUNT_PERSONAL_DETAILS_PHRASES,
    ACCOUNT_PASSWORD_CHANGE_PHRASES,
    ACCOUNT_CONTACT_DETAILS_PHRASES,
    ACCOUNT_GENERAL_PHRASES,
    ACCOUNT_PROFESSIONAL_DETAILS_PHRASES,
} from '@/consts/phrases/accountPhrases';
import {
    IAccountPersonalDetails,
    IAccountProfessionalDetails,
    IAccountPasswordChange,
    IAccountContactFormDetails,
} from '@/types/types/forms/account/account.interface';
import { ModelData } from '@/types/types/forms/modelData';
import { FORM_ERROR_PHRASES } from '@/consts/phrases/errorPhrases';

export const accountFormModel: {
    formId: string;
    [AccountTabs.PersonalDetails]: ModelData<keyof IAccountPersonalDetails>;
    [AccountTabs.ProfessionalDetails]: ModelData<keyof IAccountProfessionalDetails>;
    [AccountTabs.ContactDetails]: ModelData<keyof IAccountContactFormDetails>;
    [AccountTabs.PasswordChange]: ModelData<keyof IAccountPasswordChange>;
} = {
    formId: 'accountForm',
    [AccountTabs.PersonalDetails]: {
        firstName: {
            name: 'firstName' as const,
            label: ACCOUNT_PERSONAL_DETAILS_PHRASES.FIRST_NAME,
            requiredErrorMsg: ACCOUNT_PERSONAL_DETAILS_PHRASES.FIRST_NAME_REQUIRED_ERROR,
            invalidErrorMsg: ACCOUNT_PERSONAL_DETAILS_PHRASES.FIRST_NAME_INVALID_ERROR,
        },
        lastName: {
            name: 'lastName' as const,
            label: ACCOUNT_PERSONAL_DETAILS_PHRASES.LAST_NAME,
            requiredErrorMsg: ACCOUNT_PERSONAL_DETAILS_PHRASES.LAST_NAME_REQUIRED_ERROR,
            invalidErrorMsg: ACCOUNT_PERSONAL_DETAILS_PHRASES.LAST_NAME_INVALID_ERROR,
        },
        countryCode: {
            name: 'countryCode' as const,
            label: ACCOUNT_PERSONAL_DETAILS_PHRASES.COUNTRY,
            requiredErrorMsg: ACCOUNT_PERSONAL_DETAILS_PHRASES.COUNTRY_REQUIRED_ERROR,
        },
        zip: {
            name: 'zip' as const,
            label: ACCOUNT_PERSONAL_DETAILS_PHRASES.ZIP,
            requiredErrorMsg: ACCOUNT_PERSONAL_DETAILS_PHRASES.ZIP_REQUIRED_ERROR,
        },
        stateCode: {
            name: 'stateCode' as const,
            label: ACCOUNT_PERSONAL_DETAILS_PHRASES.STATE_CODE,
            requiredErrorMsg: ACCOUNT_PERSONAL_DETAILS_PHRASES.STATE_CODE_REQUIRED_ERROR,
        },
        city: {
            name: 'city' as const,
            label: ACCOUNT_PERSONAL_DETAILS_PHRASES.CITY,
            requiredErrorMsg: ACCOUNT_PERSONAL_DETAILS_PHRASES.CITY_REQUIRED_ERROR,
        },
        address1: {
            name: 'address1' as const,
            label: ACCOUNT_PERSONAL_DETAILS_PHRASES.ADDRESS1,
            requiredErrorMsg: ACCOUNT_PERSONAL_DETAILS_PHRASES.ADDRESS1_REQUIRED_ERROR,
        },
        address2: {
            name: 'address2' as const,
            label: ACCOUNT_PERSONAL_DETAILS_PHRASES.ADDRESS2,
        },
        preferredLanguageId: {
            name: 'preferredLanguageId' as const,
            label: ACCOUNT_PERSONAL_DETAILS_PHRASES.PREFERRED_LANGUAGE,
            requiredErrorMsg: ACCOUNT_PERSONAL_DETAILS_PHRASES.PREFERRED_LANGUAGE_REQUIRED_ERROR,
        },
        marketResearch: {
            name: 'marketResearch' as const,
            label: ACCOUNT_PERSONAL_DETAILS_PHRASES.MARKET_RESEARCH,
            requiredErrorMsg: ACCOUNT_PERSONAL_DETAILS_PHRASES.MARKET_RESEARCH_REQUIRED_ERROR,
        },
        timezoneId: {
            name: 'timezoneId' as const,
            label: ACCOUNT_PERSONAL_DETAILS_PHRASES.TIMEZONE,
        },
        nameAddressDisabled: {
            name: 'nameAddressDisabled' as const,
            label: 0,
        },
    },
    [AccountTabs.ProfessionalDetails]: {
        professionId: {
            name: 'professionId' as const,
            label: ACCOUNT_PROFESSIONAL_DETAILS_PHRASES.PROFESSION,
            requiredErrorMsg: ACCOUNT_PROFESSIONAL_DETAILS_PHRASES.PROFESSION_REQUIRED_ERROR,
        },
        specialtyId: {
            name: 'specialtyId' as const,
            label: ACCOUNT_PROFESSIONAL_DETAILS_PHRASES.SELECT_SPECIALTY,
            requiredErrorMsg: ACCOUNT_PROFESSIONAL_DETAILS_PHRASES.SPECIALTY_REQUIRED_ERROR,
        },
        medicalSchool: {
            name: 'medicalSchool' as const,
            label: ACCOUNT_PROFESSIONAL_DETAILS_PHRASES.WHAT_MEDICAL_SCHOOL,
            requiredErrorMsg: ACCOUNT_PROFESSIONAL_DETAILS_PHRASES.MEDICAL_SCHOOL_REQUIRED_ERROR,
        },
        graduationYear: {
            name: 'graduationYear' as const,
            label: ACCOUNT_PROFESSIONAL_DETAILS_PHRASES.YEAR_OF_GRADUATE,
            requiredErrorMsg: ACCOUNT_PROFESSIONAL_DETAILS_PHRASES.GRADUATION_YEAR_REQUIRED_ERROR,
        },
    },
    [AccountTabs.ContactDetails]: {
        email: {
            name: 'email' as const,
            label: ACCOUNT_CONTACT_DETAILS_PHRASES.EMAIL,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
            invalidErrorMsg: FORM_ERROR_PHRASES.ENTER_VALID_EMAIL,
        },
        emailCode: {
            name: 'emailCode' as const,
            label: ACCOUNT_GENERAL_PHRASES.TYPE_CODE,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
            invalidErrorMsg: FORM_ERROR_PHRASES.MUST_BE_A_NUMBER,
        },
        mobilePhone: {
            name: 'mobilePhone' as const,
            label: ACCOUNT_CONTACT_DETAILS_PHRASES.MOBILE_PHONE,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
            invalidErrorMsg: FORM_ERROR_PHRASES.ENTER_VALID_PHONE,
        },
        mobilePhoneCode: {
            name: 'mobilePhoneCode' as const,
            label: ACCOUNT_GENERAL_PHRASES.TYPE_CODE,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
            invalidErrorMsg: FORM_ERROR_PHRASES.MUST_BE_A_NUMBER,
        },
        mobilePhoneCodeId: {
            name: 'mobilePhoneCodeId' as const,
            label: ACCOUNT_GENERAL_PHRASES.TYPE_CODE,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
            invalidErrorMsg: FORM_ERROR_PHRASES.MUST_BE_A_NUMBER,
        },
        alternativePhone1: {
            name: 'alternativePhone1' as const,
            label: ACCOUNT_CONTACT_DETAILS_PHRASES.ALTERNATIVE_PHONE_1,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
            invalidErrorMsg: FORM_ERROR_PHRASES.ENTER_VALID_PHONE,
        },
        alternativePhone2: {
            name: 'alternativePhone2' as const,
            label: ACCOUNT_CONTACT_DETAILS_PHRASES.ALTERNATIVE_PHONE_2,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
            invalidErrorMsg: FORM_ERROR_PHRASES.ENTER_VALID_PHONE,
        },
        SMSOptIn: {
            name: 'SMSOptIn' as const,
            label: ACCOUNT_CONTACT_DETAILS_PHRASES.SMS_OPT_IN,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
        },
    },
    [AccountTabs.PasswordChange]: {
        currentPassword: {
            name: 'currentPassword' as const,
            label: ACCOUNT_PASSWORD_CHANGE_PHRASES.CURRENT_PASSWORD,
            requiredErrorMsg: ACCOUNT_PASSWORD_CHANGE_PHRASES.CURRENT_PASSWORD_REQUIRED_ERROR,
        },
        newPassword: {
            name: 'newPassword' as const,
            label: ACCOUNT_PASSWORD_CHANGE_PHRASES.NEW_PASSWORD,
            requiredErrorMsg: ACCOUNT_PASSWORD_CHANGE_PHRASES.NEW_PASSWORD_REQUIRED_ERROR,
            invalidErrorMsg: ACCOUNT_PASSWORD_CHANGE_PHRASES.NEW_PASSWORD_INVALID_ERROR,
            notEqualCurrentErrorMsg:
                ACCOUNT_PASSWORD_CHANGE_PHRASES.NEW_PASSWORD_NOT_EQUAL_CURRENT_ERROR,
            dontMatchErrorMsg: ACCOUNT_PASSWORD_CHANGE_PHRASES.NEW_PASSWORD_DONT_MATCH_ERROR,
        },
        confirmNewPassword: {
            name: 'confirmNewPassword' as const,
            label: ACCOUNT_PASSWORD_CHANGE_PHRASES.NEW_PASSWORD_CONFIRM,
            requiredErrorMsg: ACCOUNT_PASSWORD_CHANGE_PHRASES.NEW_PASSWORD_CONFIRM_REQUIRED_ERROR,
            invalidErrorMsg: ACCOUNT_PASSWORD_CHANGE_PHRASES.NEW_PASSWORD_CONFIRM_INVALID_ERROR,
            notEqualCurrentErrorMsg:
                ACCOUNT_PASSWORD_CHANGE_PHRASES.NEW_PASSWORD_CONFIRM_NOT_EQUAL_CURRENT_ERROR,
            dontMatchErrorMsg:
                ACCOUNT_PASSWORD_CHANGE_PHRASES.NEW_PASSWORD_CONFIRM_DONT_MATCH_ERROR,
        },
    },
};
